// Copyright (c) 2026 PGNS LLC

import { Card, CardContent } from '@pgns/ui/card';
import type { History, SummaryComponent } from '@/lib/api';
import {
  formatAvailability,
  formatRelative,
  groupByKind,
  historyById,
  kindLabel,
} from '@/lib/status';
import { ResponseTime } from './response-time';
import { StatusDot } from './status-dot';
import { UptimeBar } from './uptime-bar';

function ComponentRow({
  component,
  history,
  now,
}: {
  component: SummaryComponent;
  history: History | null;
  now: number;
}) {
  const days = history ? (historyById(history.components).get(component.id)?.days ?? []) : [];
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-medium text-foreground">{component.name}</h3>
            <p className="text-sm text-muted-foreground">{component.description}</p>
          </div>
          <div className="text-right">
            <StatusDot status={component.status} className="justify-end" />
            <p className="text-xs text-muted-foreground mt-1">
              checked {formatRelative(component.updated_at, now)}
            </p>
          </div>
        </div>
        <UptimeBar days={days} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            <span className="font-mono text-foreground">
              {formatAvailability(component.availability_90d)}
            </span>{' '}
            uptime over 90 days
          </p>
          <ResponseTime days={days} />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Components grouped by kind, so black-box probes and internal SLO signals read
 * as the different claims they are: one says the endpoint answered, the other
 * says deliveries actually landed.
 */
export function ComponentList({
  components,
  history,
  now,
}: {
  components: SummaryComponent[];
  history: History | null;
  now: number;
}) {
  if (components.length === 0) {
    return <p className="text-sm text-muted-foreground">No components are being monitored yet.</p>;
  }
  return (
    <div className="space-y-8">
      {groupByKind(components).map(([kind, group]) => (
        <section key={kind} className="space-y-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            {kindLabel(kind)}
          </h2>
          <div className="space-y-3">
            {group.map((component) => (
              <ComponentRow key={component.id} component={component} history={history} now={now} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
